import { get } from './client'

// ── Types ─────────────────────────────────────────────────────────────────────

export type FulfillmentCategory =
  | 'in_transit'
  | 'ready_to_ship'
  | 'blocked_has_po'
  | 'blocked_no_solution'

export type SkuFulfillmentMetric = {
  sku:                string
  title:              string
  avgFulfillmentDays: number
  stdDevDays:         number
  sampleSize:         number
  stockoutRate:       number
  isHighRisk:         boolean
  currentStock:       number
  incomingQty:        number
  nextRestockDate:    string | null
}

export type BlockingSku = {
  sku:            string
  title:          string
  requiredQty:    number
  availableQty:   number
  shortage:       number
  hasPO:          boolean
  poExpectedDate: string | null
  poIncomingQty:  number
}

export type PendingOrder = {
  orderName:            string
  createdAt:            string
  daysWaiting:          number
  codAmount:            number
  netRevenue:           number
  governorate:          string
  category:             FulfillmentCategory
  blockingSkus:         BlockingSku[]
  avgFulfillmentDays:   number | null
  expectedShipDate:     string | null
  expectedDeliveryDate: string | null
  deliversThisMonth:    boolean
}

export type PipelineSegment = {
  orderCount:       number
  totalCodAmount:   number
  totalNetRevenue:  number
  thisMonthRevenue: number
  nextMonthRevenue: number
}

export type FulfillmentPipeline = {
  inTransit:         PipelineSegment
  readyToShip:       PipelineSegment
  blockedHasPO:      PipelineSegment
  blockedNoSolution: PipelineSegment
  summary: {
    totalPendingOrders:    number
    totalPendingRevenue:   number
    thisMonthPotential:    number
    revenueAtRisk:         number
    avgGlobalDeliveryDays: number | null
  }
}

export type FulfillmentAlertType =
  | 'blocked_no_po'
  | 'po_too_late'
  | 'high_risk_sku'
  | 'slow_fulfillment'

export type FulfillmentAlert = {
  id:              string
  type:            FulfillmentAlertType
  severity:        'critical' | 'warning' | 'info'
  message:         string
  sku?:            string
  affectedOrders:  number
  revenueAtRisk:   number
  suggestedAction: string
}

export type ConfidenceLevel = 'high' | 'medium' | 'low'

export type FulfillmentIntelligence = {
  today:        string
  currentMonth: string
  monthEnd:     string
  skuMetrics:    SkuFulfillmentMetric[]
  pendingOrders: PendingOrder[]
  pipeline:      FulfillmentPipeline
  alerts:        FulfillmentAlert[]
  confidence: {
    level:       ConfidenceLevel
    score:       number
    freshness:   'live' | 'stale' | 'estimated'
    reasons:     string[]
    missingData: string[]
  }
  coverage: {
    shippedOrdersWithTimestamp: number
    shippedOrdersTotal:         number
    timestampCoveragePct:       number
    avgDeliveryDaysSample:      number
  }
}

// ── API ───────────────────────────────────────────────────────────────────────

export const fulfillmentApi = {
  getIntelligence: (): Promise<FulfillmentIntelligence> =>
    get('/forecast/fulfillment'),
}
